import { useState, useEffect, useRef } from 'react'
import api from '../../lib/api'

export function StepSync({ accountId, socket, onComplete }) {
  const [jobId, setJobId] = useState(null)
  const [progress, setProgress] = useState(null)
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const [range, setRange] = useState('30d')
  const onCompleteRef = useRef(onComplete)
  useEffect(() => { onCompleteRef.current = onComplete }, [onComplete])

  const startSync = async () => {
    setError('')
    setStatus('starting')
    try {
      const res = await api.post('/sync-jobs/start', { accountId, mailbox: 'INBOX', mode: 'initial', range })
      setJobId(res.data.jobId)
      setStatus('running')
      if (socket) socket.emit('subscribe-job', res.data.jobId)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start sync')
      setStatus('idle')
    }
  }

  useEffect(() => {
    if (!socket || !jobId) return

    const onProgress = (data) => {
      if (data.jobId === jobId) setProgress(data)
    }
    const onComplete = (data) => {
      if (data.jobId === jobId) {
        setStatus('complete')
        onCompleteRef.current?.()
      }
    }
    const onFailed = (data) => {
      if (data.jobId === jobId) {
        setStatus('failed')
        setError(data.error || 'Sync failed')
      }
    }

    socket.on('job-progress', onProgress)
    socket.on('job-complete', onComplete)
    socket.on('job-failed', onFailed)

    return () => {
      socket.off('job-progress', onProgress)
      socket.off('job-complete', onComplete)
      socket.off('job-failed', onFailed)
    }
  }, [socket, jobId])

  const total = progress?.totalEmails || 0
  const fetched = progress?.fetchedEmails || 0
  const pct = total > 0 ? Math.min(100, Math.round((fetched / total) * 100)) : 0

  return (
    <div className="onboarding-step">
      <h2>Step 3 — Sync emails</h2>
      <p className="step-hint">
        We&apos;ll fetch your inbox via IMAP. This can take a few minutes for large mailboxes.
      </p>

      {error && <div className="message error">{error}</div>}

      {status === 'idle' && (
        <div className="sync-controls">
          <label>
            How much history to fetch:{' '}
            <select value={range} onChange={(e) => setRange(e.target.value)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="3m">Last 3 months</option>
              <option value="1y">Last 1 year</option>
              <option value="all">All time</option>
            </select>
          </label>
          <button className="btn btn-primary" onClick={startSync}>Start sync</button>
        </div>
      )}

      {(status === 'starting' || status === 'running') && (
        <div className="sync-progress">
          <div className="progress-header">
            <span>{status === 'starting' ? 'Starting…' : 'Syncing…'}</span>
            <span>{pct}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="progress-stats">
            <span>{fetched.toLocaleString()} / {total.toLocaleString()} fetched</span>
            <span>{(progress?.insertedEmails || 0).toLocaleString()} inserted</span>
          </div>
        </div>
      )}

      {status === 'complete' && (
        <div className="message success">Sync complete — {(progress?.insertedEmails || 0).toLocaleString()} emails saved.</div>
      )}

      {status === 'failed' && (
        <button className="btn btn-primary" onClick={startSync}>Retry sync</button>
      )}
    </div>
  )
}
